import { ThemeEvents } from '@theme/events';

const ADDED_DURATION = 2000;
const ERROR_DURATION = 6000;

/**
 * Sticky add-to-cart bar for product pages.
 *
 * It never adds to cart on its own. It mirrors the main product form in the
 * product information section (label, availability, price, variant) and submits
 * that form, so quantity, selected variant, markets logic, sold-out states and
 * the cart drawer all keep working exactly as with the main button.
 *
 * The bar only becomes visible when the main buy buttons have been scrolled
 * past, and hides again when the footer comes into view.
 */
class StickyAddToCart extends HTMLElement {
  #abort = new AbortController();
  /** @type {HTMLElement | null} */ #grid = null;
  /** @type {HTMLElement | null} */ #section = null;
  /** @type {HTMLElement | null} */ #mainForm = null;
  /** @type {HTMLButtonElement | null} */ #mainButton = null;
  /** @type {Element | null} */ #observedTarget = null;
  /** @type {Element | null} */ #footer = null;
  /** @type {IntersectionObserver | null} */ #io = null;
  /** @type {MutationObserver | null} */ #mo = null;
  #mainInView = true;
  #footerInView = false;
  #available = false;
  #pending = false;
  #raf = 0;
  /** @type {number | undefined} */ #addedTimer;
  /** @type {number | undefined} */ #errorTimer;

  connectedCallback() {
    this.#grid = document.querySelector('#MainContent [data-product-grid-content]');
    if (!this.#grid) return;

    this.#section = this.#grid.closest('.shopify-section');
    this.button = /** @type {HTMLButtonElement} */ (this.querySelector('[data-sticky-atc-button]'));
    this.labelEl = this.querySelector('[data-sticky-atc-label]');
    this.priceEl = this.querySelector('[data-sticky-atc-price]');
    this.variantEl = this.querySelector('[data-sticky-atc-variant]');
    this.imageEl = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-sticky-atc-image]'));
    this.errorEl = this.querySelector('[data-sticky-atc-error]');
    this.expressEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-sticky-atc-express]'));
    this.expressIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-sticky-atc-express-id]'));
    this.expressQtyInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-sticky-atc-express-qty]'));

    if (!this.button) return;

    this.#io = new IntersectionObserver(this.#onIntersect, { threshold: 0 });
    this.#footer = document.querySelector('.shopify-section-group-footer-group, footer');
    if (this.#footer) this.#io.observe(this.#footer);

    this.#sync();

    // Nothing to mirror (dealer CTA, market not sellable, etc.) – stay hidden.
    if (!this.#mainForm) return;

    this.hidden = false;
    this.inert = true;

    const { signal } = this.#abort;
    this.button.addEventListener('click', this.#onClick, { signal });
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    window.addEventListener(ThemeEvents.cartError, this.#onCartError, { signal });
    window.addEventListener('resize', this.#updateOffset, { signal, passive: true });
    // Quantity changes are value changes, which a MutationObserver cannot see.
    this.#grid.addEventListener('change', this.#scheduleSync, { signal });
    this.#grid.addEventListener('input', this.#scheduleSync, { signal });
    this.#grid.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#scheduleSync, { signal });

    this.#mo = new MutationObserver(this.#scheduleSync);
    this.#mo.observe(this.#grid, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#io?.disconnect();
    this.#mo?.disconnect();
    cancelAnimationFrame(this.#raf);
    clearTimeout(this.#addedTimer);
    clearTimeout(this.#errorTimer);
    document.documentElement.classList.remove('sticky-atc-visible');
  }

  #scheduleSync = () => {
    cancelAnimationFrame(this.#raf);
    this.#raf = requestAnimationFrame(() => this.#sync());
  };

  /** Copies state from the main product form into the bar. */
  #sync() {
    if (!this.#grid) return;

    const form = /** @type {HTMLElement | null} */ (this.#grid.querySelector('product-form-component'));
    const button = /** @type {HTMLButtonElement | null} */ (form?.querySelector('[ref="addToCartButton"]') ?? null);

    this.#mainForm = form;
    this.#mainButton = button;

    const target = form?.closest('.buy-buttons-block') ?? form;
    if (target !== this.#observedTarget) {
      if (this.#observedTarget) this.#io?.unobserve(this.#observedTarget);
      if (target) this.#io?.observe(target);
      this.#observedTarget = target ?? null;
    }

    if (!button) {
      this.#available = false;
      this.#update();
      return;
    }

    this.#available = !button.disabled;

    // Match the main button's style (primary/secondary) and corner radius.
    this.button?.classList.toggle('button-secondary', button.classList.contains('button-secondary'));
    const radius = getComputedStyle(button).borderRadius;
    if (radius) this.style.setProperty('--sticky-atc-radius', radius);

    this.#syncExpress(form);

    const label = button.querySelector('.add-to-cart-text__content')?.textContent?.trim();
    if (label && this.labelEl && this.labelEl.textContent !== label) this.labelEl.textContent = label;

    const price = this.#grid.querySelector('product-price [ref="priceContainer"]');
    if (price && this.priceEl && this.priceEl.innerHTML !== price.innerHTML) {
      this.priceEl.innerHTML = price.innerHTML;
    }

    this.#update();
  }

  /**
   * Shows the wallet button only when the main buy buttons show one,
   * and keeps its variant and quantity in step with the main form.
   * @param {HTMLElement | null} form
   */
  #syncExpress(form) {
    if (!this.expressEl) {
      this.classList.remove('sticky-atc--has-express');
      return;
    }

    const mainExpress = /** @type {HTMLElement | null} */ (
      form?.querySelector('[ref="acceleratedCheckoutButtonContainer"]') ?? null
    );
    const hasExpress = Boolean(mainExpress) && !mainExpress?.hidden && !mainExpress?.hasAttribute('hidden');

    if (this.expressEl.hidden === hasExpress) this.expressEl.hidden = !hasExpress;
    if (this.classList.contains('sticky-atc--has-express') !== hasExpress) {
      this.classList.toggle('sticky-atc--has-express', hasExpress);
      this.#updateOffset();
    }

    const mainId = /** @type {HTMLInputElement | null} */ (form?.querySelector('form input[name="id"]') ?? null);
    if (mainId?.value && this.expressIdInput && this.expressIdInput.value !== mainId.value) {
      this.expressIdInput.value = mainId.value;
      this.expressIdInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const mainQty = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="quantity"]') ?? null);
    const qty = mainQty?.value || form?.dataset.quantityDefault || '1';
    if (this.expressQtyInput && this.expressQtyInput.value !== qty) {
      this.expressQtyInput.value = qty;
      this.expressQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (entry.target === this.#footer) {
        this.#footerInView = entry.isIntersecting;
      } else {
        // Treat the buy buttons as "in view" until the visitor has scrolled past them.
        this.#mainInView = entry.isIntersecting || entry.boundingClientRect.top > 0;
      }
    }
    this.#update();
  };

  #update() {
    const show = Boolean(this.#mainForm) && this.#available && !this.#mainInView && !this.#footerInView;
    if (this.classList.contains('is-visible') === show) return;

    this.classList.toggle('is-visible', show);
    this.inert = !show;
    document.documentElement.classList.toggle('sticky-atc-visible', show);
    this.#updateOffset();
  }

  /** Exposes the bar height so other floating elements (chat, back-to-top) can offset themselves. */
  #updateOffset = () => {
    const height = this.classList.contains('is-visible') ? this.offsetHeight : 0;
    document.documentElement.style.setProperty('--sticky-atc-height', `${height}px`);
  };

  #onClick = () => {
    const form = this.#mainForm?.querySelector('form');
    if (!form || !this.#mainButton || this.#mainButton.disabled) {
      this.#scrollToMain();
      return;
    }

    this.#clearError();
    this.#pending = true;

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(this.#mainButton);
    } else {
      this.#mainButton.click();
    }
  };

  /** @param {CustomEvent} event */
  #onCartUpdate = (event) => {
    if (!this.#pending) return;
    const data = event.detail?.data ?? {};
    if (data.source !== 'product-form-component') return;
    if (this.#mainForm?.dataset.productId && String(data.productId) !== String(this.#mainForm.dataset.productId)) return;

    this.#pending = false;
    if (data.didError) return;

    clearTimeout(this.#addedTimer);
    this.button?.classList.add('atc-added');
    this.#addedTimer = setTimeout(() => this.button?.classList.remove('atc-added'), ADDED_DURATION);
  };

  /** @param {CustomEvent} event */
  #onCartError = (event) => {
    if (!this.#pending || !this.errorEl) return;
    this.#pending = false;

    const message = event.detail?.data?.message;
    if (!message) return;

    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
    this.#updateOffset();
    clearTimeout(this.#errorTimer);
    this.#errorTimer = setTimeout(() => this.#clearError(), ERROR_DURATION);
  };

  #clearError() {
    if (!this.errorEl || this.errorEl.hidden) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
    this.#updateOffset();
  }

  /** @param {CustomEvent} event */
  #onVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    if (!variant) return;

    if (this.variantEl && variant.title) {
      this.variantEl.textContent = variant.title;
    }

    if (variant.id && this.expressIdInput && this.expressIdInput.value !== String(variant.id)) {
      this.expressIdInput.value = String(variant.id);
      this.expressIdInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const src = variant.featured_media?.preview_image?.src;
    if (src && this.imageEl) {
      try {
        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', '160');
        this.imageEl.src = url.toString();
      } catch (e) {
        this.imageEl.src = src;
      }
    }

    this.#scheduleSync();
  };

  #scrollToMain() {
    const target = this.#observedTarget ?? this.#grid;
    target?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'center',
    });
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
